import { Dialog, DialogContent } from '@/components/ui/dialog';
import { X } from 'lucide-react';
import { useJackpot } from '@/contexts/JackpotContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useState } from 'react';

interface JackpotOptInPromptModalProps {
  isOpen: boolean;
  onOptInAndPlay: () => void;
  onPlayWithoutOptIn: () => void;
  onClose: () => void;
}

export const JackpotOptInPromptModal = ({
  isOpen,
  onOptInAndPlay,
  onPlayWithoutOptIn,
  onClose,
}: JackpotOptInPromptModalProps) => {
  const { toggleOptIn, getJackpotsForCurrency } = useJackpot();
  const { formatCurrency } = useTheme();
  const [isOptingIn, setIsOptingIn] = useState(false);
  
  const jackpots = getJackpotsForCurrency('SC');
  const optInCost = jackpots.grand.optInCost;
  const grandAmount = jackpots.grand.amount;
  
  const formatAmount = (amount: number) => {
    if (amount >= 1_000_000) {
      return (amount / 1_000_000).toFixed(1) + 'M';
    } else if (amount >= 1_000) {
      return Math.floor(amount).toLocaleString();
    }
    return amount.toFixed(2);
  };

  const handleOptInAndPlay = async () => {
    setIsOptingIn(true);
    try {
      await toggleOptIn('SC');
      onOptInAndPlay();
    } catch {
      onOptInAndPlay();
    } finally {
      setIsOptingIn(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-[320px] w-[90vw] p-0 overflow-hidden border-0 bg-transparent shadow-none [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div
          className="relative rounded-2xl overflow-hidden"
          style={{
            background: 'linear-gradient(165deg, #1a1a2e 0%, #0f1419 50%, #1a1a2e 100%)',
            boxShadow: '0 0 80px rgba(16, 185, 129, 0.25), 0 25px 50px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
          }}
        >
          {/* Animated glow effect */}
          <div 
            className="absolute inset-0 opacity-40"
            style={{
              background: 'radial-gradient(ellipse at 50% 0%, rgba(16, 185, 129, 0.3) 0%, transparent 60%)',
            }}
          />
          
          {/* Top shimmer line */}
          <div 
            className="absolute top-0 inset-x-0 h-[2px]"
            style={{
              background: 'linear-gradient(90deg, transparent, #22c55e, #10b981, #22c55e, transparent)',
            }}
          />

          <div className="relative z-10 px-6 pt-6 pb-5">
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-3 right-3 p-1.5 rounded-full text-gray-500 hover:text-gray-300 hover:bg-white/10 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Main prize amount */}
            <div className="text-center">
              <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">Win up to</div>
              <div 
                className="text-3xl font-black tracking-tight"
                style={{
                  background: 'linear-gradient(135deg, #22c55e 0%, #4ade80 50%, #22c55e 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 0 20px rgba(34, 197, 94, 0.4))',
                }}
              >
                {formatAmount(grandAmount)} SC
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Only {formatCurrency(optInCost, 'SC')} SC opt in per spin
              </div>
            </div>

            {/* CTA Button */}
            <button
              onClick={handleOptInAndPlay}
              disabled={isOptingIn}
              className="w-full mt-5 py-3.5 rounded-xl font-bold text-sm transition-all duration-200 active:scale-[0.98] disabled:opacity-70"
              style={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                color: 'white',
                boxShadow: '0 4px 20px rgba(34, 197, 94, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
              }}
            >
              {isOptingIn ? 'Enabling...' : 'Enable Jackpot & Play'}
            </button>

            {/* Skip option */}
            <button
              onClick={onPlayWithoutOptIn}
              disabled={isOptingIn}
              className="w-full mt-2 py-2 text-[13px] text-gray-500 hover:text-gray-300 transition-colors"
            >
              No thanks
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
